import React, { useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { styles } from './RangeSliderRefinement.css';
import CheckboxSaleItems from '../CheckboxToggleRefinement/CheckboxSaleItems';
import CheckboxClearanceItems from '../CheckboxToggleRefinement/CheckboxClearanceItems';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import { postAlgoliaFilterView } from '../../../../utils/algolia/search-insights';
import PriceRangeSlider from '../PriceRangeSlider/PriceRangeSlider';

interface RangeSliderRefinementProps {
  attribute: string;
  facet: string;
  nbHits: number;
  defaultRefinement: any;
  setDisableScroll?: any;
  trackingInfo: any;
  onDropdownToggle?: (isOpen: boolean) => void;
  nbSaleItems: number;
  nbClearanceItems: number;
  isSearchPage?: boolean;
}

const RangeSliderRefinement = ({
  attribute,
  facet,
  nbHits,
  defaultRefinement,
  setDisableScroll,
  trackingInfo,
  onDropdownToggle,
  nbSaleItems,
  nbClearanceItems,
  isSearchPage = false,
}: RangeSliderRefinementProps) => {
  const RefineButton = useRef<HTMLButtonElement | null>(null);
  const RefineDropdown = useRef<HTMLDivElement | null>(null);

  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const [currentRefinement, setCurrentRefinement] = useState({ isRefined: false, min: 0, max: 0 });
  const [isSaleItemsRefined, setIsSaleItemsRefined] = useState(false);
  const [isClearanceItemsRefined, setIsClearanceItemsRefined] = useState(false);

  const searchState = qs.parse(window.location.search.slice(1));

  // Remove the event listener on unmount
  useEffect(() => {
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, []);

  /**
   * Function to handle external clicks (clicks not within the price range dropdown or button)
   */
  const externalClick = (event: MouseEvent | PointerEvent) => {
    const PriceRangeButton = RefineButton.current;
    const RangeSliderRefinement = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (
      !event ||
      (RangeSliderRefinement &&
        !RangeSliderRefinement.contains(event.target as Node) &&
        PriceRangeButton &&
        !PriceRangeButton.contains(event.target as Node))
    ) {
      setShouldShowDropdown(false);
      setDisableScroll?.(false);
    }
  };

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = (event: { preventDefault: () => void }) => {
    onDropdownToggle?.(!shouldShowDropdown);
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: 'price',
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterView('price');
    event.preventDefault();
    setShouldShowDropdown(!shouldShowDropdown);
  };

  useEffect(() => {
    if (shouldShowDropdown) {
      document.body.addEventListener('pointerdown', externalClick);
      setDisableScroll?.(true);
    } else {
      document.body.removeEventListener('pointerdown', externalClick);
      setDisableScroll?.(false);
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, [shouldShowDropdown]);

  /**
   * Render function
   */
  return (
    <div className="search-results-facet" data-testid="range-slider-refinement">
      <button
        type="button"
        className={`search-results-facet-button${shouldShowDropdown ? ' active' : ''}${
          currentRefinement.isRefined || isSaleItemsRefined || isClearanceItemsRefined ? ' refined' : ''
        }`}
        ref={RefineButton}
        onClick={toggleDropdown}
      >
        {facet}
        {!isSearchPage
          ? currentRefinement.isRefined &&
            currentRefinement?.min &&
            currentRefinement?.max &&
            ` (${facet === 'Price' ? '$' : ''}${currentRefinement.min} - ${facet === 'Price' ? '$' : ''}${
              currentRefinement.max
            })`
          : ''}
      </button>
      <div
        ref={RefineDropdown}
        className={`search-results-facet-dropdown scoped-form-styles ${shouldShowDropdown ? 'active' : ''}`}
      >
        <div className="resultNum">
          {nbHits} result{nbHits !== 1 ? 's' : ''}
        </div>
        <PriceRangeSlider
          attribute={attribute}
          defaultRefinement={defaultRefinement}
          shouldShowDollar={facet === 'Price'}
          nbHits={nbHits}
          setCurrentRefinement={setCurrentRefinement}
          shouldShowDropdown={shouldShowDropdown}
          trackingInfo={trackingInfo}
        />

        {facet === 'Price' && (
          <div className={styles.checkboxToggleContainer}>
            <CheckboxSaleItems
              label="Sale items"
              setIsSaleItemsRefined={setIsSaleItemsRefined}
              nbSaleItems={nbSaleItems}
              trackingInfo={trackingInfo}
            />
            <CheckboxClearanceItems
              label="Clearance"
              setIsClearanceItemsRefined={setIsClearanceItemsRefined}
              nbClearanceItems={nbClearanceItems}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RangeSliderRefinement;
